/**
 * Material Design Icons -> Phosphor (Regular) name table.
 *
 * BS Labs draws every icon with Phosphor Regular. The app still names ~670
 * distinct `mdi-*` icons across ~275 files, so rather than editing each call
 * site the Vuetify icon set (vuetifyPhosphorSet.ts) looks the name up here and
 * draws the Phosphor glyph instead of the MDI font glyph.
 *
 * Keys are MDI names WITHOUT the `mdi-` prefix. `-outline` variants resolve to
 * their base name (Phosphor Regular is already an outline style), so a key is
 * only needed for an `-outline` name when it should differ from its base.
 * Mapped by meaning, not spelling: mdi-delete is a bin, so it is `trash`.
 *
 * A name that is missing here keeps rendering from the MDI font, so a new or
 * dynamic icon never draws as a blank box. Add the name here (and run
 * `node scripts/icons/generate-phosphor-registry.mjs`) to move it over.
 */

export const MDI_TO_PHOSPHOR: Record<string, string> = {
    // People
    account: 'user',
    'account-badge': 'identification-badge',
    'account-cash': 'hand-coins',
    'account-check': 'user-check',
    'account-clock': 'clock-user',
    'account-cog': 'user-gear',
    'account-edit': 'user-gear',
    'account-eye': 'user-focus',
    'account-group': 'users-three',
    'account-hard-hat': 'hard-hat',
    'account-heart': 'hand-heart',
    'account-multiple': 'users',
    'account-multiple-plus': 'user-plus',
    'account-off': 'user-minus',
    'account-plus': 'user-plus',
    'account-search': 'user-focus',
    'account-star': 'user-circle',
    'account-tie': 'user-circle',

    // Status and feedback
    alert: 'warning',
    'alert-circle': 'warning-circle',
    'alert-octagon': 'warning-octagon',
    'alert-plus': 'warning',
    cancel: 'prohibit',
    check: 'check',
    'check-all': 'checks',
    'check-bold': 'check',
    'check-circle': 'check-circle',
    'check-decagram': 'seal-check',
    close: 'x',
    'close-circle': 'x-circle',
    exclamation: 'exclamation-mark',
    'help-circle': 'question',
    'frequently-asked-questions': 'question',
    information: 'info',
    'information-variant': 'info',
    loading: 'spinner-gap',
    'progress-check': 'check-circle',
    'progress-clock': 'clock-countdown',
    'progress-upload': 'upload-simple',
    'shield-account': 'shield',
    'shield-alert': 'shield-warning',
    'shield-check': 'shield-check',
    'shield-lock': 'shield',
    'thumb-down': 'thumbs-down',
    'thumb-up': 'thumbs-up',

    // Checkbox-style glyphs used as decoration (Vuetify's own checkbox and
    // radio controls keep MDI: see vuetifyPhosphorSet.ts).
    'checkbox-blank': 'square',
    // The outline ring maps; the filled dots below it are left on MDI.
    'checkbox-blank-circle-outline': 'circle',
    'checkbox-marked': 'check-square',
    'checkbox-marked-circle': 'check-circle',
    'checkbox-marked-circle-plus': 'check-circle',
    'checkbox-multiple': 'check-square-offset',
    'checkbox-multiple-blank': 'copy-simple',
    'checkbox-multiple-marked': 'check-square-offset',
    'circle-outline': 'circle',
    'circle-half': 'circle-half',
    'minus-box': 'minus-square',

    // Arrows, chevrons and carets
    'arrow-collapse': 'arrows-in',
    'arrow-collapse-horizontal': 'arrows-in-line-horizontal',
    'arrow-down': 'arrow-down',
    'arrow-expand': 'arrows-out',
    'arrow-expand-horizontal': 'arrows-out-line-horizontal',
    'arrow-expand-vertical': 'arrows-out-line-vertical',
    'arrow-left': 'arrow-left',
    'arrow-left-bold-box': 'arrow-square-left',
    'arrow-right': 'arrow-right',
    'arrow-right-bold-box': 'arrow-square-right',
    'arrow-right-circle': 'arrow-circle-right',
    'arrow-split-vertical': 'arrows-split',
    'arrow-top-right': 'arrow-up-right',
    'arrow-up': 'arrow-up',
    'arrow-up-circle': 'arrow-circle-up',
    'axis-z-rotate-counterclockwise': 'arrow-counter-clockwise',
    'chevron-double-left': 'caret-double-left',
    'chevron-double-right': 'caret-double-right',
    'chevron-down': 'caret-down',
    'chevron-left': 'caret-left',
    'chevron-right': 'caret-right',
    'chevron-up': 'caret-up',
    'menu-down': 'caret-down',
    'menu-right': 'caret-right',
    'page-first': 'caret-line-left',
    'page-last': 'caret-line-right',
    'unfold-more-horizontal': 'caret-up-down',
    'subdirectory-arrow-right': 'arrow-elbow-down-right',
    'swap-horizontal': 'arrows-left-right',
    'swap-horizontal-bold': 'arrows-left-right',
    'swap-vertical': 'arrows-down-up',
    reply: 'arrow-bend-up-left',
    redo: 'arrow-clockwise',
    undo: 'arrow-counter-clockwise',
    restore: 'arrow-counter-clockwise',
    refresh: 'arrows-clockwise',
    cached: 'arrows-clockwise',
    sync: 'arrows-clockwise',
    'sync-alert': 'arrows-clockwise',
    'open-in-new': 'arrow-square-out',
    'application-import': 'arrow-square-in',
    import: 'arrow-square-in',
    export: 'export',

    // Actions
    plus: 'plus',
    'plus-box': 'plus-square',
    'plus-circle': 'plus-circle',
    'plus-thick': 'plus',
    minus: 'minus',
    'minus-circle': 'minus-circle',
    multiplication: 'x',
    delete: 'trash',
    'trash-can': 'trash',
    'delete-sweep': 'broom',
    pencil: 'pencil-simple',
    'pencil-box': 'note-pencil',
    'pencil-box-multiple': 'note-pencil',
    'pencil-circle': 'pencil-circle',
    'pencil-plus': 'pencil-simple',
    pen: 'pen',
    draw: 'pencil-line',
    'draw-pen': 'pen-nib',
    rename: 'pencil-simple-line',
    eraser: 'eraser',
    marker: 'highlighter',
    'content-copy': 'copy',
    'content-duplicate': 'copy-simple',
    'content-save': 'floppy-disk',
    'content-save-alert': 'floppy-disk',
    'content-save-check': 'floppy-disk',
    download: 'download-simple',
    upload: 'upload-simple',
    'upload-off': 'upload-simple',
    'tray-arrow-down': 'tray-arrow-down',
    send: 'paper-plane-right',
    'send-check': 'paper-plane-tilt',
    'send-clock': 'paper-plane-tilt',
    'share-variant': 'share-network',
    printer: 'printer',
    magnify: 'magnifying-glass',
    'magnify-close': 'magnifying-glass',
    'magnify-minus': 'magnifying-glass-minus',
    'magnify-plus': 'magnifying-glass-plus',
    'magnify-remove': 'magnifying-glass',
    'magnify-scan': 'scan',
    filter: 'funnel',
    'filter-remove': 'funnel-x',
    'filter-variant': 'funnel-simple',
    tune: 'sliders-horizontal',
    'tune-variant': 'sliders-horizontal',
    'tune-vertical': 'sliders',
    eye: 'eye',
    'eye-off': 'eye-slash',
    eyedropper: 'eyedropper',
    link: 'link',
    'link-off': 'link-break',
    'link-variant': 'link-simple',
    'link-variant-off': 'link-simple-break',
    'link-variant-plus': 'link-simple',
    paperclip: 'paperclip',
    lock: 'lock',
    'lock-open': 'lock-open',
    key: 'key',
    'key-plus': 'key',
    'key-variant': 'key',
    play: 'play',
    'play-circle': 'play-circle',
    'pause-circle': 'pause-circle',
    fullscreen: 'corners-out',
    'fit-to-screen': 'frame-corners',
    'flip-horizontal': 'flip-horizontal',
    'flip-vertical': 'flip-vertical',
    'mirror-rectangle': 'flip-horizontal',
    'auto-fix': 'magic-wand',
    creation: 'sparkle',
    shimmer: 'sparkle',
    'dots-horizontal': 'dots-three',
    'dots-vertical': 'dots-three-vertical',
    drag: 'dots-six-vertical',
    'drag-vertical': 'dots-six-vertical',
    'drag-horizontal-variant': 'dots-six',
    'drag-variant': 'arrows-out-cardinal',
    menu: 'list',
    star: 'star',
    'star-half-full': 'star-half',
    heart: 'heart',
    'heart-half': 'heart-half',
    'heart-pulse': 'heartbeat',
    'bookmark-plus': 'bookmark-simple',
    flag: 'flag',
    'flag-checkered': 'flag-checkered',
    target: 'target',
    history: 'clock-counter-clockwise',
    update: 'clock-clockwise',

    // Time and calendars
    calendar: 'calendar-blank',
    'calendar-blank': 'calendar-blank',
    'calendar-account': 'calendar-blank',
    'calendar-alert': 'calendar-dot',
    'calendar-arrow-right': 'calendar-blank',
    'calendar-check': 'calendar-check',
    'calendar-clock': 'calendar-dots',
    'calendar-edit': 'calendar-blank',
    'calendar-end': 'calendar-blank',
    'calendar-month': 'calendar',
    'calendar-multiple': 'calendar-dots',
    'calendar-multiple-check': 'calendar-check',
    'calendar-multiselect': 'calendar-dots',
    'calendar-plus': 'calendar-plus',
    'calendar-range': 'calendar',
    'calendar-remove': 'calendar-x',
    'calendar-star': 'calendar-star',
    'calendar-start': 'calendar-blank',
    'calendar-sync': 'calendar-dots',
    'calendar-text': 'calendar',
    'calendar-today': 'calendar-dot',
    'calendar-week': 'calendar',
    'calendar-weekend': 'calendar',
    clock: 'clock',
    'clock-alert': 'clock',
    'clock-check': 'clock',
    'clock-fast': 'timer',
    'clock-in': 'sign-in',
    'clock-plus': 'clock',
    'clock-time-eight': 'clock',
    'timer-sand': 'hourglass',

    // Files, folders and documents
    file: 'file',
    'file-alert': 'file',
    'file-chart': 'file',
    'file-check': 'file',
    'file-code': 'file-code',
    'file-delimited': 'file-csv',
    'file-document': 'file-text',
    'file-document-alert': 'file-text',
    'file-document-arrow-right': 'file-text',
    'file-document-check': 'file-text',
    'file-document-edit': 'note-pencil',
    'file-document-multiple': 'files',
    'file-document-plus': 'file-plus',
    'file-excel': 'file-xls',
    'file-export': 'file-arrow-up',
    'file-eye': 'file-magnifying-glass',
    'file-image': 'file-image',
    'file-import': 'file-arrow-down',
    'file-move': 'file-arrow-up',
    'file-multiple': 'files',
    'file-pdf-box': 'file-pdf',
    'file-powerpoint': 'file-ppt',
    'file-restore': 'file',
    'file-search': 'file-magnifying-glass',
    'file-sign': 'signature',
    'file-tree': 'tree-structure',
    'file-upload': 'file-arrow-up',
    'file-word': 'file-doc',
    folder: 'folder',
    'folder-alert': 'folder',
    'folder-check': 'folder',
    'folder-eye': 'folder',
    'folder-multiple': 'folders',
    'folder-multiple-image': 'images',
    'folder-open': 'folder-open',
    'folder-plus': 'folder-plus',
    'folder-question': 'folder',
    'folder-zip': 'file-zip',
    'clipboard-alert': 'clipboard',
    'clipboard-check': 'clipboard-text',
    'clipboard-list': 'clipboard-text',
    'clipboard-text': 'clipboard-text',
    'clipboard-text-clock': 'clipboard-text',
    'clipboard-text-off': 'clipboard',
    'note-edit': 'note-pencil',
    'note-plus': 'note-blank',
    'note-text': 'note',
    notebook: 'notebook',
    'notebook-edit': 'notebook',
    book: 'book',
    'book-alert': 'book',
    'book-open-page-variant': 'book-open',
    'book-open-variant': 'book-open-text',
    bookshelf: 'books',
    'text-box': 'article',
    'text-box-check': 'article',
    'text-box-remove': 'article',
    'text-box-search': 'list-magnifying-glass',
    'text-search': 'list-magnifying-glass',
    archive: 'archive',
    receipt: 'receipt',
    'receipt-text': 'receipt',
    certificate: 'certificate',
    signature: 'signature',
    'code-json': 'brackets-curly',
    database: 'database',
    'database-import': 'database',
    'database-remove': 'database',
    image: 'image',
    'image-off': 'image-broken',
    inbox: 'tray',

    // Text formatting and lists
    'form-textbox': 'textbox',
    'format-bold': 'text-b',
    'format-header-pound': 'text-h',
    'format-italic': 'text-italic',
    'format-list-bulleted': 'list-bullets',
    'format-list-bulleted-square': 'list-bullets',
    'format-list-bulleted-type': 'list-dashes',
    'format-list-checks': 'list-checks',
    'format-list-numbered': 'list-numbers',
    'format-paint': 'paint-roller',
    'format-text': 'text-t',
    'format-title': 'text-h',
    'format-underline': 'text-underline',
    'list-status': 'list-checks',
    'playlist-edit': 'list-dashes',
    'playlist-plus': 'list-plus',
    text: 'text-align-left',
    'text-recognition': 'text-aa',
    numeric: 'hash',
    'numeric-1-circle': 'number-circle-one',
    counter: 'hash',
    equal: 'equals',
    percent: 'percent',
    sigma: 'sigma',
    'function-variant': 'function',

    // Layout, tables and views
    table: 'table',
    'table-arrow-left': 'table',
    'table-cog': 'table',
    'table-column': 'columns',
    'table-column-plus-after': 'columns-plus-right',
    'table-large': 'table',
    'table-off': 'table',
    'table-row-plus-after': 'rows-plus-bottom',
    'table-row-plus-before': 'rows-plus-top',
    tab: 'tabs',
    grid: 'grid-four',
    'view-column': 'columns',
    'view-dashboard': 'squares-four',
    'view-grid': 'grid-four',
    'view-list': 'list',
    'view-module': 'squares-four',
    'view-sequential': 'rows',
    layers: 'stack',
    'layers-triple': 'stack',

    // Charts
    'chart-bar': 'chart-bar',
    'chart-box': 'chart-bar',
    'chart-gantt': 'chart-bar-horizontal',
    'chart-line': 'chart-line',
    'chart-line-variant': 'chart-line-up',
    'chart-pie': 'chart-pie',
    'chart-timeline-variant': 'chart-line',
    'trending-down': 'trend-down',
    'trending-up': 'trend-up',
    speedometer: 'gauge',

    // Communication
    bell: 'bell',
    'bell-alert': 'bell-ringing',
    'bell-check': 'bell',
    'bell-off': 'bell-slash',
    'bell-ring': 'bell-ringing',
    bullhorn: 'megaphone',
    comment: 'chat-circle',
    'comment-check': 'chat-circle',
    'comment-question': 'chat-circle-dots',
    'comment-text': 'chat-circle-text',
    'comment-text-multiple': 'chats-circle',
    message: 'chat',
    'message-processing': 'chat-dots',
    'message-question': 'chat-dots',
    'message-text': 'chat-text',
    'message-text-clock': 'chat-text',
    email: 'envelope-simple',
    'email-alert': 'envelope-simple',
    'email-arrow-right': 'paper-plane-tilt',
    'email-check': 'envelope-simple',
    'email-fast': 'paper-plane-tilt',
    'email-off': 'envelope-simple',
    'email-open': 'envelope-simple-open',
    'email-send': 'paper-plane-tilt',
    'email-sync': 'envelope-simple',
    phone: 'phone',
    cellphone: 'device-mobile',
    microphone: 'microphone',
    'microphone-off': 'microphone-slash',
    api: 'plugs-connected',
    ethernet: 'network',
    qrcode: 'qr-code',

    // Places, buildings and maps
    home: 'house',
    'home-account': 'house',
    'home-circle': 'house',
    'home-city': 'city',
    'home-modern': 'house-simple',
    'home-roof': 'house-line',
    domain: 'buildings',
    'office-building': 'building-office',
    'office-building-cog': 'building-office',
    'floor-plan': 'blueprint',
    map: 'map-trifold',
    'map-marker': 'map-pin',
    'map-marker-alert': 'map-pin',
    'map-marker-off': 'map-pin',
    'map-marker-path': 'path',
    'map-marker-radius': 'map-pin-area',
    compass: 'compass',
    'hospital-box': 'first-aid-kit',
    ambulance: 'ambulance',
    garage: 'garage',
    hallway: 'door-open',
    door: 'door',
    stairs: 'stairs',
    wall: 'wall',
    fence: 'barricade',
    pillar: 'columns',
    truss: 'bridge',
    scaffold: 'ladder',
    forest: 'tree-evergreen',
    flower: 'flower',
    pool: 'swimming-pool',
    beach: 'umbrella-simple',

    // Construction, trades and tools
    'hard-hat': 'hard-hat',
    excavator: 'bulldozer',
    shovel: 'shovel',
    'hammer-wrench': 'hammer',
    tools: 'toolbox',
    wrench: 'wrench',
    'wrench-clock': 'wrench',
    cog: 'gear',
    'cog-sync': 'gear',
    ruler: 'ruler',
    'ruler-square': 'ruler',
    'ruler-square-compass': 'compass-tool',
    'tape-measure': 'ruler',
    'angle-acute': 'angle',
    'scale-balance': 'scales',
    weight: 'scales',
    calculator: 'calculator',
    'calculator-variant': 'calculator',
    bucket: 'paint-bucket',
    palette: 'palette',
    spray: 'spray-bottle',
    texture: 'checkerboard',
    opacity: 'drop-half',
    pipe: 'pipe',
    'fire-extinguisher': 'fire-extinguisher',
    'smoke-detector': 'siren',
    'solar-panel': 'solar-panel',
    'power-socket-au': 'plug',
    'light-switch': 'toggle-left',
    'lightning-bolt': 'lightning',
    flash: 'lightning',
    lightbulb: 'lightbulb',
    'lightbulb-on': 'lightbulb-filament',
    'lightbulb-spot': 'lightbulb',
    'air-conditioner': 'fan',
    'snowflake-thermometer': 'thermometer-cold',
    thermometer: 'thermometer',
    water: 'drop',
    'water-off': 'drop-slash',
    magnet: 'magnet',
    'magnet-on': 'magnet-straight',

    // Rooms and fixtures
    bathtub: 'bathtub',
    shower: 'shower',
    'shower-head': 'shower',
    sink: 'drop',
    toilet: 'toilet',
    bed: 'bed',
    sofa: 'couch',
    desk: 'desk',
    'table-furniture': 'desk',
    cabinet: 'dresser',
    wardrobe: 'coat-hanger',
    stove: 'oven',
    iron: 't-shirt',
    'washing-machine': 'washing-machine',
    'window-closed': 'grid-four',
    'window-closed-variant': 'grid-four',
    television: 'television',
    'baby-carriage': 'baby-carriage',
    'food-variant': 'fork-knife',

    // Drawing and takeoff tools
    'cursor-default': 'cursor',
    gesture: 'scribble',
    'gesture-swipe-horizontal': 'hand-swipe-left',
    'gesture-tap': 'hand-tap',
    'hand-back-left': 'hand',
    'hand-back-right': 'hand',
    'hand-heart': 'hand-heart',
    'mouse-right-click': 'mouse-right-click',
    'vector-curve': 'bezier-curve',
    'vector-line': 'line-segment',
    'vector-polygon': 'polygon',
    'vector-polyline': 'line-segments',
    'vector-rectangle': 'rectangle',
    'vector-square': 'bounding-box',
    rectangle: 'rectangle',
    square: 'square',
    triangle: 'triangle',
    pentagon: 'pentagon',
    shape: 'shapes',
    'shape-plus': 'shapes',
    'shape-polygon-plus': 'polygon',
    cube: 'cube',

    // Money and commerce
    'currency-usd': 'currency-dollar',
    'cash-alert': 'money',
    'cash-check': 'money',
    'cash-clock': 'money',
    'cash-multiple': 'money-wavy',
    'credit-card': 'credit-card',
    'credit-card-off': 'credit-card',
    'piggy-bank': 'piggy-bank',
    wallet: 'wallet',
    cart: 'shopping-cart',
    tag: 'tag',
    'tag-multiple': 'tag',
    'tag-off': 'tag',
    'tag-plus': 'tag',
    'tag-search': 'tag',
    gift: 'gift',
    trophy: 'trophy',
    box: 'package',
    'package-variant': 'package',
    'package-variant-remove': 'package',
    truck: 'truck',
    'truck-delivery': 'truck',
    briefcase: 'briefcase',
    'briefcase-account': 'briefcase',
    'briefcase-check': 'briefcase',
    'briefcase-clock': 'briefcase',
    'briefcase-edit': 'briefcase',
    'briefcase-off': 'briefcase',
    'briefcase-plus': 'briefcase',
    'briefcase-search': 'briefcase',

    // Devices, cloud and misc
    camera: 'camera',
    'camera-enhance': 'camera-plus',
    laptop: 'laptop',
    keyboard: 'keyboard',
    cloud: 'cloud',
    'cloud-check': 'cloud-check',
    'cloud-download': 'cloud-arrow-down',
    'cloud-off': 'cloud-slash',
    'cloud-upload': 'cloud-arrow-up',
    'microsoft-onedrive': 'cloud',
    'google-drive': 'google-drive-logo',
    dropbox: 'dropbox-logo',
    facebook: 'facebook-logo',
    instagram: 'instagram-logo',
    linkedin: 'linkedin-logo',
    'source-branch': 'git-branch',
    robot: 'robot',
    'robot-confused': 'robot',
    'robot-happy': 'robot',
    'rocket-launch': 'rocket-launch',
    brain: 'brain',
    flask: 'flask',
    lifebuoy: 'lifebuoy',
    'brightness-6': 'circle-half',

    // Weather (site diaries)
    'weather-cloudy': 'cloud',
    'weather-cloudy-alert': 'cloud-warning',
    'weather-fog': 'cloud-fog',
    'weather-lightning-rainy': 'cloud-lightning',
    'weather-partly-cloudy': 'cloud-sun',
    'weather-pouring': 'cloud-rain',
    'weather-rainy': 'cloud-rain',
    'weather-snowy': 'cloud-snow',
    'weather-sunny': 'sun',
    'weather-windy': 'wind'
};

/**
 * Names deliberately left on the MDI font. These are FILLED dots (chart
 * legends, status markers drawn at 8-10px); Phosphor Regular `circle` is a
 * ring, which would read as "empty / not set" instead of a coloured marker.
 */
export const MDI_KEEP_FONT = new Set(['circle', 'circle-medium', 'circle-small', 'checkbox-blank-circle']);

/**
 * MDI CSS helper classes that ride along in an icon string (e.g.
 * `icon="mdi-loading mdi-spin"`). They are not icon names.
 */
const MDI_MODIFIER = /^mdi-(spin|light|dark|inactive|\d+px|rotate-\d+|flip-[hv])$/;

export interface ParsedMdi {
    /** The MDI name without the `mdi-` prefix, or null when the string names no MDI icon. */
    name: string | null;
    /** Every other class in the string (`mdi`, `mdi-spin`, custom classes) to keep on the element. */
    extraClasses: string[];
}

/** Split an icon string such as `"mdi mdi-checkbox-blank-circle"` into its icon name and other classes. */
export function parseMdi(icon: string): ParsedMdi {
    let name: string | null = null;
    const extraClasses: string[] = [];
    for (const token of icon.trim().split(/\s+/)) {
        if (!name && token.startsWith('mdi-') && !MDI_MODIFIER.test(token)) {
            name = token.slice(4);
        } else if (token && token !== 'mdi') {
            extraClasses.push(token);
        }
    }
    return { name, extraClasses };
}

/**
 * The Phosphor name for an MDI name (with or without `mdi-`), or null when it
 * should keep rendering from the MDI font.
 */
export function mdiToPhosphor(mdiName: string): string | null {
    const name = mdiName.startsWith('mdi-') ? mdiName.slice(4) : mdiName;
    if (MDI_KEEP_FONT.has(name)) return null;
    if (MDI_TO_PHOSPHOR[name]) return MDI_TO_PHOSPHOR[name];
    if (name.endsWith('-outline')) {
        const base = name.slice(0, -'-outline'.length);
        if (!MDI_KEEP_FONT.has(base) && MDI_TO_PHOSPHOR[base]) return MDI_TO_PHOSPHOR[base];
    }
    return null;
}
